// src/pages/Appli/AppliAdmin/NotificationList.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  getNotificationsByUser,
  deleteNotificationById,
} from "../../../services/notificationServices";
import { useNotification } from "../../../components/Notification/NotificationManager";
import useScrollToTop from "../../../hooks/useScollToTop";
import {
  FaBell,
  FaSearch,
  FaTimes,
  FaTrash,
  FaChevronLeft,
  FaChevronRight,
  FaClock,
  FaCalendarAlt,
} from "react-icons/fa";

/* ================= Helpers ================= */
const PAGE_SIZE = 20;

const normalize = (s) =>
  (s || "")
    .toString()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

const useDebounced = (value, delay = 250) => {
  const [v, setV] = useState(value);

  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);

  return v;
};

function formatDateTimeFR(date) {
  if (!date) return "Date inconnue";
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "Date inconnue";

  return d.toLocaleString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function isToday(date) {
  if (!date) return false;
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return false;

  const now = new Date();
  return (
    d.getDate() === now.getDate() &&
    d.getMonth() === now.getMonth() &&
    d.getFullYear() === now.getFullYear()
  );
}

function isThisWeek(date) {
  if (!date) return false;
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return false;

  const now = new Date();
  const start = new Date(now);
  const day = start.getDay() || 7; // lundi = 1 ... dimanche = 7
  start.setDate(now.getDate() - day + 1);
  start.setHours(0, 0, 0, 0);

  const end = new Date(start);
  end.setDate(start.getDate() + 7);

  return d >= start && d < end;
}

/* ================= Header ================= */
function CompactStat({ icon, label, value, accent = "indigo" }) {
  const accents = {
    indigo:
      "border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300",
    emerald:
      "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300",
    amber:
      "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300",
  };

  return (
    <div className={`rounded-2xl border px-3 py-3 ${accents[accent]}`}>
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/80 dark:bg-slate-900/50">
          {icon}
        </div>
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-[0.16em] opacity-75">
            {label}
          </div>
          <div className="mt-0.5 text-xl font-bold text-slate-900 dark:text-white">
            {value}
          </div>
        </div>
      </div>
    </div>
  );
}

function AdminPageHeader({ total, todayCount, weekCount }) {
  return (
    <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-4 sm:p-5 lg:p-6">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Administration
            </p>
            <h1 className="mt-1 text-2xl font-bold text-slate-900 sm:text-[30px] dark:text-white">
              Notifications
            </h1>
            <p className="mt-2 max-w-3xl text-sm text-slate-600 dark:text-slate-300">
              Historique des notifications reçues avec consultation rapide et
              suppression individuelle.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-3 xl:min-w-[620px]">
            <CompactStat
              icon={<FaBell />}
              label="Notifications"
              value={total}
              accent="indigo"
            />
            <CompactStat
              icon={<FaClock />}
              label="Aujourd’hui"
              value={todayCount}
              accent="emerald"
            />
            <CompactStat
              icon={<FaCalendarAlt />}
              label="Cette semaine"
              value={weekCount}
              accent="amber"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ================= Filters ================= */
function FiltersBar({
  searchTerm,
  setSearchTerm,
  onReset,
  loading,
  totalCount,
}) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr),auto]">
        <div className="relative">
          <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Rechercher dans les notifications..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="h-11 w-full rounded-2xl border border-slate-300 bg-white pl-10 pr-4 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
          />
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            onClick={onReset}
            disabled={loading}
            className="inline-flex h-11 items-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <FaTimes />
            Réinitialiser
          </button>

          <span className="rounded-full bg-slate-100 px-3 py-2 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
            {totalCount} résultat{totalCount > 1 ? "s" : ""}
          </span>
        </div>
      </div>
    </div>
  );
}

/* ================= Table desktop ================= */
function NotificationTableDesktop({ rows, loading, deletingId, onDelete }) {
  return (
    <div className="hidden lg:block">
      <div className="rounded-3xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="overflow-x-auto rounded-3xl">
          <table className="w-full min-w-[980px] table-fixed text-sm">
            <thead className="bg-slate-50 text-left dark:bg-slate-950/60">
              <tr className="text-slate-600 dark:text-slate-300">
                <th className="w-[58%] px-4 py-3 font-semibold">Message</th>
                <th className="w-[22%] px-4 py-3 font-semibold">Date</th>
                <th className="w-[20%] px-4 py-3 text-right font-semibold">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
              {loading ? (
                <tr>
                  <td colSpan={3} className="px-4 py-10 text-center text-slate-500">
                    Chargement des notifications…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-4 py-10 text-center text-slate-500">
                    Aucune notification disponible.
                  </td>
                </tr>
              ) : (
                rows.map((notification) => (
                  <tr
                    key={notification.id}
                    className="align-top transition hover:bg-slate-50/80 dark:hover:bg-slate-800/40"
                  >
                    <td className="px-4 py-3.5">
                      <div className="text-[14px] leading-6 text-slate-800 dark:text-slate-100">
                        {notification.message || "Message vide"}
                      </div>
                    </td>

                    <td className="px-4 py-3.5">
                      <div className="text-[13px] text-slate-600 dark:text-slate-300">
                        {formatDateTimeFR(notification.created_at)}
                      </div>
                    </td>

                    <td className="px-4 py-3.5">
                      <div className="flex justify-end">
                        <button
                          type="button"
                          onClick={() => onDelete(notification.id)}
                          disabled={deletingId === notification.id}
                          className="inline-flex h-10 items-center gap-2 rounded-2xl border border-red-200 bg-red-50 px-4 text-sm font-semibold text-red-700 transition hover:bg-red-100 disabled:opacity-50 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300 dark:hover:bg-red-500/20"
                        >
                          <FaTrash />
                          {deletingId === notification.id
                            ? "Suppression..."
                            : "Supprimer"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ================= Mobile cards ================= */
function NotificationCardsMobile({ rows, loading, deletingId, onDelete }) {
  return (
    <div className="lg:hidden">
      {loading ? (
        <div className="rounded-3xl border border-slate-200 bg-white px-4 py-10 text-center text-slate-500 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          Chargement des notifications…
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-3xl border border-slate-200 bg-white px-4 py-10 text-center text-slate-500 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          Aucune notification disponible.
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((notification) => (
            <div
              key={notification.id}
              className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900"
            >
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300">
                  <FaBell />
                </div>

                <div className="min-w-0 flex-1">
                  <div className="text-[14px] leading-6 text-slate-800 dark:text-slate-100">
                    {notification.message || "Message vide"}
                  </div>

                  <div className="mt-2 text-[12px] text-slate-500 dark:text-slate-400">
                    {formatDateTimeFR(notification.created_at)}
                  </div>
                </div>
              </div>

              <div className="mt-4 border-t border-slate-100 pt-4 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => onDelete(notification.id)}
                  disabled={deletingId === notification.id}
                  className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-2xl border border-red-200 bg-red-50 px-4 text-sm font-semibold text-red-700 transition hover:bg-red-100 disabled:opacity-50 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300 dark:hover:bg-red-500/20"
                >
                  <FaTrash />
                  {deletingId === notification.id
                    ? "Suppression..."
                    : "Supprimer"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ================= Pagination ================= */
function PaginationBar({ page, pageCount, setPage, loading }) {
  if (pageCount <= 1) return null;

  return (
    <div className="mt-5 flex flex-col items-center justify-between gap-3 rounded-3xl border border-slate-200 bg-white px-4 py-4 sm:flex-row dark:border-slate-800 dark:bg-slate-900">
      <div className="text-sm text-slate-500 dark:text-slate-400">
        Page <span className="font-semibold text-slate-900 dark:text-white">{page}</span>{" "}
        sur{" "}
        <span className="font-semibold text-slate-900 dark:text-white">
          {pageCount}
        </span>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page === 1 || loading}
          className="inline-flex h-10 items-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
        >
          <FaChevronLeft />
          Précédent
        </button>

        <button
          type="button"
          onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
          disabled={page === pageCount || loading}
          className="inline-flex h-10 items-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
        >
          Suivant
          <FaChevronRight />
        </button>
      </div>
    </div>
  );
}

/* ================= Page ================= */
const NotificationList = () => {
  useScrollToTop();

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);

  const { addNotification } = useNotification();
  const debouncedSearch = useDebounced(searchTerm, 250);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const data = await getNotificationsByUser(1);
      setNotifications(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erreur lors de la récupération des notifications :", error);
      addNotification(
        "Erreur lors de la récupération des notifications.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteNotification = async (id) => {
    const confirmed = window.confirm(
      "Souhaitez-vous vraiment supprimer cette notification ?"
    );
    if (!confirmed) return;

    try {
      setDeletingId(id);
      await deleteNotificationById(id);

      setNotifications((prev) => prev.filter((notification) => notification.id !== id));
      addNotification("Notification supprimée avec succès.", "success");
    } catch (error) {
      console.error("Erreur lors de la suppression de la notification :", error);
      addNotification(
        "Erreur lors de la suppression de la notification.",
        "error"
      );
    } finally {
      setDeletingId(null);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const sortedNotifications = useMemo(() => {
    return [...notifications].sort((a, b) => {
      const ta = a?.created_at ? new Date(a.created_at).getTime() : 0;
      const tb = b?.created_at ? new Date(b.created_at).getTime() : 0;
      return tb - ta;
    });
  }, [notifications]);

  const filteredNotifications = useMemo(() => {
    const nq = normalize(debouncedSearch);

    return sortedNotifications.filter((notification) => {
      if (!nq) return true;

      return normalize(
        [notification?.message, notification?.created_at].join(" ")
      ).includes(nq);
    });
  }, [sortedNotifications, debouncedSearch]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const totalNotifications = filteredNotifications.length;
  const todayCount = useMemo(
    () => filteredNotifications.filter((n) => isToday(n.created_at)).length,
    [filteredNotifications]
  );
  const weekCount = useMemo(
    () => filteredNotifications.filter((n) => isThisWeek(n.created_at)).length,
    [filteredNotifications]
  );

  const pageCount = Math.max(1, Math.ceil(filteredNotifications.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);

  const paginatedNotifications = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return filteredNotifications.slice(start, start + PAGE_SIZE);
  }, [filteredNotifications, safePage]);

  const handleReset = () => {
    setSearchTerm("");
    setPage(1);
  };

  return (
    <div className="w-full pb-10 pt-[96px] font-satoshi">
      <div className="mx-auto max-w-[1400px] px-4">
        <AdminPageHeader
          total={totalNotifications}
          todayCount={todayCount}
          weekCount={weekCount}
        />

        <div className="mt-5">
          <FiltersBar
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            onReset={handleReset}
            loading={loading}
            totalCount={totalNotifications}
          />
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
          <span className="rounded-full bg-slate-100 px-3 py-1 dark:bg-slate-800">
            {totalNotifications} notification{totalNotifications > 1 ? "s" : ""}
          </span>

          {debouncedSearch && (
            <span className="rounded-full bg-indigo-50 px-3 py-1 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
              Recherche : {searchTerm}
            </span>
          )}

          {!loading && totalNotifications > 0 && (
            <span className="ml-auto text-xs">
              Affichage {paginatedNotifications.length} • page {safePage} / {pageCount}
            </span>
          )}
        </div>

        <div className="mt-5">
          <NotificationTableDesktop
            rows={paginatedNotifications}
            loading={loading}
            deletingId={deletingId}
            onDelete={handleDeleteNotification}
          />

          <NotificationCardsMobile
            rows={paginatedNotifications}
            loading={loading}
            deletingId={deletingId}
            onDelete={handleDeleteNotification}
          />
        </div>

        <PaginationBar
          page={safePage}
          pageCount={pageCount}
          setPage={setPage}
          loading={loading}
        />
      </div>
    </div>
  );
};

export default NotificationList;